import { useCallback, useEffect, useRef } from 'react';

// Long enough to outlast the AntD collapse animation (motionDeadline: 500ms).
const SETTLE_MS = 900;
// Consecutive frames with an unchanged panel height before we call it settled.
const STABLE_FRAMES = 3;

/**
 * Reveals a collapse section downward: pins its header below the sticky nav and
 * holds it there while the panel animates open, so the content always grows
 * beneath the title instead of shoving the page around.
 *
 * A single scroll fired at click time is not enough. rc-collapse mounts panel
 * content lazily and animates its height up from 0, so at that moment the
 * document is still at its old height — for a section near the bottom of the
 * page the browser clamps the scroll to `scrollHeight - innerHeight` and the
 * header never reaches the top. Once the panel expands the document is tall
 * enough, but nothing scrolls again. That clamp is what makes sections appear to
 * open upward, and only for the sections low enough in the page to hit it.
 *
 * Vertical placement comes from `scroll-margin-top` on the header (_collapse.scss),
 * so there is no offset hardcoded here.
 *
 * @returns {(sectionId: string) => void} Call with the anchor id of the section being opened.
 */
export const useRevealSectionDownward = () => {
  const rafRef = useRef(null);
  const cleanupRef = useRef(null);

  const stop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  useEffect(() => stop, [stop]);

  return useCallback(
    (sectionId) => {
      stop();

      const startedAt = performance.now();
      let lastHeight = -1;
      let stableFrames = 0;

      // The user always wins: any manual scroll input hands control back.
      // Listening on 'wheel'/'touchstart'/'keydown' rather than 'scroll' so our
      // own scrolling below doesn't cancel the pin.
      const release = () => stop();
      window.addEventListener('wheel', release, { passive: true });
      window.addEventListener('touchstart', release, { passive: true });
      window.addEventListener('keydown', release);
      cleanupRef.current = () => {
        window.removeEventListener('wheel', release);
        window.removeEventListener('touchstart', release);
        window.removeEventListener('keydown', release);
      };

      const tick = () => {
        const anchor = document.getElementById(sectionId);
        if (!anchor) {
          stop();
          return;
        }

        const header = anchor.closest('.ant-collapse-header') || anchor;
        header.scrollIntoView({ block: 'start', behavior: 'auto' });

        // The panel may not be mounted yet for the first few frames; keep
        // pinning until it appears and its height stops growing.
        const content = header
          .closest('.ant-collapse-item')
          ?.querySelector('.ant-collapse-content');
        const height = content ? content.getBoundingClientRect().height : -1;

        stableFrames = height === lastHeight ? stableFrames + 1 : 0;
        lastHeight = height;

        const settled = height > 0 && stableFrames >= STABLE_FRAMES;
        if (settled || performance.now() - startedAt > SETTLE_MS) {
          stop();
          return;
        }

        rafRef.current = requestAnimationFrame(tick);
      };

      rafRef.current = requestAnimationFrame(tick);
    },
    [stop],
  );
};

export default useRevealSectionDownward;
